import React from 'react';
import { icons } from '../../utils/svgPaths';
import './HomePage.css';

// ── Hero Section ─────────────────────────────────────────────────────────────
function HeroSection() {
  return (
    <section
      className="hero-section"
    >
      <div
        className="hero-inner"
      >
      {/* ── Left column: text content ── */}
      <div className="hero-left">
        {/* Badge */}
        <div className="hero-badge">
          <span className="hero-badge-text">Excelencia en cada puntada</span>
        </div>

        {/* Headline */}
        <div className="hero-headline">
          <h1 className="hero-h1 hero-h1--dark">Bordados con</h1>
          <h1 className="hero-h1 hero-h1--teal">Identidad Única</h1>
        </div>

        {/* Description */}
        <p className="hero-desc">
          Somos una empresa costarricense con más de 18 años de trascendencia en el mercado de la costura y el diseño e implementación de bordados.
        </p>

        {/* Bullet list */}
        <div className="hero-bullets">
          {[
            'Trabajos bajo pedidos',
            'Diseños 100% personalizados',
            'Compromiso, responsabilidad y honestidad',
          ].map((item) => (
            <div key={item} className="hero-bullet-item">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="hero-bullet-svg">
                <circle cx="12" cy="12" r="10" stroke="#06AC9E" strokeWidth="2" />
                <path d="M9 12L11 14L15 10" stroke="#06AC9E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="hero-bullet-text">{item}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right column: circle bg + image + stats card ── */}
      <div className="hero-right">
        {/* Green circle with threads image */}
        <div
          className="hero-circle"
        >
          {/* Light green ellipse background */}
          <div className="hero-ellipse" />
          {/* Threads image — overflows the circle slightly at top */}
          <img
            className="hero-threads-img"
            src="/images/threads.png"
            alt="Hilos de bordado"
          />
        </div>

        {/* Stats card + "Calidad que se siente" — below the image */}
        <div className="hero-stats-row">
          <div className="hero-stats-card">
            <div className="hero-stats-number">+18</div>
            <div className="hero-stats-label">Años de Experiencia</div>
          </div>
          <span className="hero-quality-text">Calidad que se siente</span>
        </div>
      </div>
      </div>
    </section>
  );
}

// ── Services Label ────────────────────────────────────────────────────────────
function ServicesLabel() {
  return (
    <div className="services-label">
      <div className="services-label-inner">
        <p className="services-label-title">Nuestros servicios:</p>
        <p className="services-label-sub">Confección y bordado de prendas de alta calidad</p>
      </div>
    </div>
  );
}

// ── Service Cards ─────────────────────────────────────────────────────────────
const serviceCards = [
  {
    icon: (
      <svg width="38" height="38" viewBox="0 0 51 51" fill="none">
        <path d={icons.shirt} fill="#8FAE8A" />
      </svg>
    ),
    title: 'Bordado personalizado',
    description:
      'Personalizamos uniformes, camisas, jackets, gorras y más con bordados de alta calidad. Tu logo o diseño quedará profesional, resistente y con excelente acabado.',
    image: '/images/needleworks.jpeg',
    isVideo: false,
  },
  {
    icon: (
      <svg width="42" height="42" viewBox="0 0 58 58" fill="none">
        <path d={icons.sewingMachine} fill="#8FAE8A" />
      </svg>
    ),
    title: 'Mantenimiento de equipos de costura',
    description:
      'También ofrecemos mantenimiento preventivo y correctivo de máquinas de costura, asegurando su funcionamiento óptimo y prolongando su vida útil.',
    image: '/images/fixed_machine.jpeg',
    isVideo: false,
  },
  {
    icon: (
      <svg width="38" height="38" viewBox="0 0 49 49" fill="none">
        <path d={icons.scissors} fill="#8FAE8A" />
      </svg>
    ),
    title: 'Confección de prendas',
    description:
      'Diseñamos y confeccionamos prendas escolares, empresariales, deportivas e institucionales adaptadas a tus necesidades.',
    image: '/images/clothes.jpeg',
    isVideo: false,
  },
];

function ServiceCards() {
  return (
    <section className="service-cards-section">
      <div className="service-cards-outer">
      <div className="service-cards-row">
        {serviceCards.map((card) => (
          <div
            key={card.title}
            className="service-card"
          >
            {/* Fade-in media overlay */}
            {card.isVideo ? (
              <video
                src={card.image}
                autoPlay
                loop
                muted
                playsInline
                className="service-card-media"
              />
            ) : (
              <img
                src={card.image}
                alt={card.title}
                className="service-card-media"
              />
            )}
            {/* Dark overlay for readability on hover */}
            <div className="service-card-dark-overlay" />
            {/* Content */}
            <div className="service-card-icon">{card.icon}</div>
            <h3 className="service-card-title">{card.title}</h3>
            <p className="service-card-desc">
              {card.description}
            </p>
          </div>
        ))}
      </div>
      </div>
    </section>
  );
}

// ── By Why Choose Us ──────────────────────────────────────────────────────────
const whyItems = [
  'Más de 18 años de experiencia',
  'Entregas responsables y puntuales',
  'Atención personalizada',
  'Bordados duraderos y profesionales',
  'Materiales de calidad',
  'Nos comprometemos a que cada cliente quede satisfecho, porque tu confianza es nuestro mayor respaldo.',
];

function CheckIcon() {
  return (
    <div className="check-icon-wrapper">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="12" fill="#06AC9E" />
        <path d="M7 12.5L10 15.5L17 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function WhyChooseUs() {
  return (
    <section className="why-section">
      <h2 className="why-title">¿Por qué elegirnos?</h2>
      <div className="why-inner">
        {/* Items list */}
        <div className="why-list">
          {whyItems.map((item) => (
            <div key={item} className="why-item">
              <CheckIcon />
              <p className="why-item-p">{item}</p>
            </div>
          ))}
        </div>
        {/* Video */}
        <video
          className="why-video"
          src="/images/needlework.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
      </div>
    </section>
  );
}

// ── Contact + Compromise ──────────────────────────────────────────────────────
function ContactCompromiso() {
  return (
    <section className="contact-section">
      <div className="contact-cards-row">
        {/* Contacto */}
        <div className="contact-card">
          <h3 className="contact-card-title">Contacto</h3>
          <p className="contact-card-text">
            Estamos ubicados en Alajuela, Orotina Centro. Contáctenos para más información sobre nuestros servicios.
          </p>
          <div className="contact-info-list">
            {[
              { label: 'Teléfono', value: '8910-5489' },
              { label: 'Email', value: 'gemevm@gmail.com' },
              {
                label: 'Dirección',
                value: 'Alajuela, Orotina Centro, entrada vieja a Orotina, 400 m norte de las antiguas oficinas del ICE.',
              },
            ].map(({ label, value }) => (
              <p key={label} className="contact-card-text">
                <span className="contact-bold-label">{label}: </span>
                {value}
              </p>
            ))}
          </div>
        </div>

        {/* Nuestro compromiso */}
        <div className="contact-card">
          <h3 className="contact-card-title">Nuestro compromiso</h3>
          <p className="contact-card-text">
            Confeccionamos y bordamos prendas con personal capacitado y comprometido. Nuestro objetivo es tener un cliente satisfecho que pueda alcanzar sus propios objetivos.
          </p>
          <p className="contact-card-text">
            Trabajamos con altos estándares de calidad cuidando cada detalle para ofrecer productos que reflejen dedicación, experiencia y confianza en cada proyecto que nos encargan.
          </p>
        </div>
      </div>
    </section>
  );
}

// ── Page Export ───────────────────────────────────────────────────────────────
export default function HomePage() {
  return (
    <main>
      <HeroSection />
      <ServicesLabel />
      <ServiceCards />
      <WhyChooseUs />
      <ContactCompromiso />
    </main>
  );
}
